/**
 * Token metadata — governance layer over `themeTokens.ts`.
 *
 * Every token has a status. Tokens without an explicit entry default to
 * `active`. Surface `proposed` and `deprecated` tokens in the design library
 * so they are addressed and don't pile up.
 *
 * Path conventions follow dot-notation matching the shape of `defaultThemeTokens`:
 *   "brand.primary", "backgrounds.card", "typography.h1", etc.
 */

export type TokenStatus = "active" | "proposed" | "deprecated" | "needs-review";

export interface TokenMetadata {
  status: TokenStatus;
  /** One-line purpose. Shown next to the swatch/value in the design library. */
  description?: string;
  /** The corresponding Figma variable name, if mapped. */
  figmaName?: string;
  /** Whether the value is expected to vary by theme/tenant. */
  themeable?: boolean;
  /** Contrast / a11y guidance (e.g. "AA on white"). */
  accessibilityNotes?: string;
  /** For `proposed`: why it was added and what gating it. */
  proposalNote?: string;
  /** For `deprecated`: which token replaces it. */
  replacedBy?: string;
  /** For `deprecated`: ISO date by which it should be removed. */
  removeBy?: string;
}

const DEFAULT_METADATA: TokenMetadata = { status: "active" };

const TOKEN_METADATA: Record<string, TokenMetadata> = {
  // ── Brand ──────────────────────────────────────────────────────────────
  "brand.primary": {
    status: "active",
    description: "Primary brand surface and headline color.",
    figmaName: "Color/Brand/Primary",
    themeable: true,
  },
  "brand.primaryHover": {
    status: "active",
    description: "Hover state for primary brand surfaces.",
    figmaName: "Color/Brand/Primary Hover",
    themeable: true,
  },
  "brand.accent": {
    status: "active",
    description:
      "Account accent, themeable per account. Read by a small set of learner surfaces (Ask Ema button/panel header, Welcome pop-up header, timer welcome-back modal header, per-lesson video retry button) — pair with text.onAccent for a contrast-safe foreground.",
    figmaName: "Color/Brand/Accent",
    themeable: true,
  },
  "brand.accentHover": {
    status: "active",
    description: "Hover state for accent-colored surfaces.",
    themeable: true,
  },

  // ── Backgrounds ────────────────────────────────────────────────────────
  "backgrounds.app": { status: "active", description: "Outer application background." },
  "backgrounds.page": { status: "active", description: "Page-level background inside the app shell." },
  "backgrounds.courseShell": { status: "active", description: "Background for the runtime course shell." },
  "backgrounds.surface": { status: "active", description: "Default raised surface (cards, panels)." },
  "backgrounds.surfaceAlt": { status: "active", description: "Alternate raised surface for contrast against `surface`." },
  "backgrounds.lessonSectionDefault": { status: "active", description: "Default lesson section background." },
  "backgrounds.lessonSectionTinted": { status: "active", description: "Tinted lesson section background for alternation." },
  "backgrounds.inverse": { status: "active", description: "Dark inverse surface (used by editor sidebar)." },
  "backgrounds.card": { status: "active", description: "Card surface (lighter than `surface`)." },
  "backgrounds.cardAlt": { status: "active", description: "Alternate card surface for contrast." },
  "backgrounds.resultsPanel": { status: "active", description: "Background for interaction results panels." },
  "backgrounds.sidebar": { status: "active", description: "Editor sidebar background (chrome)." },
  "backgrounds.sidebarLogo": {
    status: "proposed",
    description: "Background tile behind the client logo at the bottom of the left sidebar — slightly darker than `backgrounds.sidebar` for contrast.",
    figmaName: "Theme Settings / Logo background in left panel",
    proposalNote: "NA-76 Theme Settings: client-configurable independent of `buttons.action.bg`. Default value (#1D1D1F) is intentionally darker than the panel surround (`backgrounds.sidebar`) for contrast behind the logo. Carved out as a separate token so future client overrides change only the logo well, not the action button color. Initial figma source: node 4436:1496 (NA-102).",
  },
  "backgrounds.dot": { status: "active", description: "Modal active carousel dot" },

  // ── Text ───────────────────────────────────────────────────────────────
  "text.primary": {
    status: "active",
    description: "Default body and heading text color.",
    accessibilityNotes: "AA on backgrounds.app/page/card.",
  },
  "text.muted": {
    status: "active",
    description: "Secondary/explanatory text.",
    accessibilityNotes: "AA on backgrounds.app/page only.",
  },
  "text.subtle": {
    status: "active",
    description: "Tertiary text — labels, hints.",
    accessibilityNotes: "Use for non-essential text only; sub-AA on white.",
  },
  "text.callout": { status: "active", description: "Color for stylized text callouts." },
  "text.onBrand": { status: "active", description: "Text rendered over `brand.primary`." },
  "text.onAccent": {
    status: "active",
    description:
      "Text/icon color for surfaces built on `brand.accent`. Computed per-account (NA-294) via getReadableTextColor rather than static, so it stays readable when an account picks a light accent.",
    themeable: true,
    accessibilityNotes: "Derived to meet WCAG AA against brand.accent — do not hardcode white at accent call sites.",
  },
  "text.onInverse": { status: "active", description: "Text rendered over `backgrounds.inverse` (sidebar)." },
  "text.link": {
    status: "active",
    description: "Inline link color.",
    accessibilityNotes: "Verify AA on each background it appears over.",
  },

  // ── Borders ────────────────────────────────────────────────────────────
  "borders.default": { status: "active", description: "Default control/container border." },
  "borders.inverse": {
    status: "needs-review",
    description: "Editor sidebar border — currently outside the learner-facing design-system scope. Decide whether to keep here or move to a chrome-specific namespace.",
  },
  "borders.interactive": { status: "active", description: "Border for interactive controls and drop zones." },
  "borders.dropZone": { status: "active", description: "Single Choice empty drop zone (dashed)." },

  // ── Buttons ────────────────────────────────────────────────────────────
  "buttons.action.bg": { status: "active", description: "Primary action button background (idle)." },
  "buttons.action.hover": { status: "active", description: "Primary action button background (hover)." },
  "buttons.action.disabledBg": { status: "active", description: "Primary action button background (disabled)." },
  "buttons.action.disabledFg": { status: "active", description: "Primary action button text/icon color (disabled)." },

  // ── Choice states ──────────────────────────────────────────────────────
  "choice.hover.border": { status: "active", description: "Hovered choice border (also used for drag)." },
  "choice.hover.bg": { status: "active", description: "Hovered choice fill." },
  "choice.drag.border": { status: "active", description: "Choice border while being dragged." },
  "choice.drag.bg": { status: "active", description: "Choice fill while being dragged." },
  "choice.wrong.border": { status: "active", description: "Incorrect-answer border." },
  "choice.wrong.bg": { status: "active", description: "Incorrect-answer fill." },
  "choice.correct.border": { status: "active", description: "Correct-answer border." },
  "choice.correct.bg": { status: "active", description: "Correct-answer fill." },

  // ── Semantic ───────────────────────────────────────────────────────────
  "semantic.success": { status: "active", description: "Success text/icon color." },
  "semantic.successBg": { status: "active", description: "Success surface fill." },
  "semantic.error": { status: "active", description: "Error text/icon color." },
  "semantic.errorBg": { status: "active", description: "Error surface fill." },

  // ── Workplace Color Spectrum ──────────────────────────────────────────
  "wcs.green": { status: "active", description: "WCS healthy band." },
  "wcs.yellow": { status: "active", description: "WCS attention band." },
  "wcs.yellowLegend": {
    status: "needs-review",
    description: "Darker yellow intended for legend swatches (pure yellow is illegible at small sizes). No source references found — confirm whether the legend is built or remove this token.",
  },
  "wcs.orange": { status: "active", description: "WCS warning band." },
  "wcs.red": { status: "active", description: "WCS critical band." },

  // ── Bar chart ──────────────────────────────────────────────────────────
  "bar.myOrg": { status: "active", description: "Bar chart series — current organization." },
  "bar.global": { status: "active", description: "Bar chart series — global benchmark." },
  // ── Banner ─────────────────────────────────────────────────────────────
  "banner.info.background": {
    status: "proposed",
    description: "Background fill for informational banners (e.g. client-context banner shown when viewing as a specific account).",
    proposalNote: "NA-78 client context dropdown: introduced to give banners their own semantic token pair rather than reusing bar chart colors. Light blue wash distinct from bar.global which is too saturated for a full-width banner background.",
  },
  "banner.info.border": {
    status: "proposed",
    description: "Border color for informational banners.",
    proposalNote: "NA-78 client context dropdown: pairs with banner.info.background. Medium blue for clear visual separation without high contrast.",
  },

  // ── Likert ─────────────────────────────────────────────────────────────
  "likert.negative.strong": { status: "active", description: "Likert — strongly negative response." },
  "likert.negative.medium": { status: "active", description: "Likert — moderately negative response." },
  "likert.negative.light": { status: "active", description: "Likert — slightly negative response." },
  "likert.neutral": { status: "active", description: "Likert — neutral response." },
  "likert.positive.light": { status: "active", description: "Likert — slightly positive response." },
  "likert.positive.medium": { status: "active", description: "Likert — moderately positive response." },
  "likert.positive.strong": { status: "active", description: "Likert — strongly positive response." },

  // ── Badge ──────────────────────────────────────────────────────────────
  "badge.rosette": { status: "active", description: "Achievement rosette fill." },
  "badge.star": { status: "active", description: "Star color sitting on the rosette." },
  "badge.ribbon": { status: "active", description: "Ribbon fill." },
  "badge.ribbonShadow": { status: "active", description: "Ribbon shadow / fold color." },
  "badge.size": { status: "active", description: "Default rendered badge size." },

  // ── Completion ─────────────────────────────────────────────────────────
  "completion.accent": {
    status: "active",
    description: "Accent for completion surfaces — sidebar top border, banner background, lesson row check tile.",
  },
  "completion.label": {
    status: "active",
    description: "Subtitle color used in completed-lesson labels.",
  },

  // ── Typography (named scales) ──────────────────────────────────────────
  "typography.fontFamily": {
    status: "active",
    description: "Application font stack. Source Sans 3 + system fallbacks.",
  },
  "typography.h1": { status: "active", description: "Largest heading." },
  "typography.h2": { status: "active", description: "Section heading." },
  "typography.h3": { status: "active", description: "Sub-section heading." },
  "typography.h4": { status: "active", description: "Block / minor heading." },
  "typography.body": { status: "active", description: "Default body copy." },
  "typography.display": { status: "active", description: "Display weight for hero/marketing surfaces." },
  "typography.callout": { status: "active", description: "Default callout type style." },
  "typography.lessonTitle": { status: "active", description: "Lesson page title." },
  "typography.calloutSm": { status: "active", description: "Small callout style." },
  "typography.calloutMd": { status: "active", description: "Medium callout style." },
  "typography.calloutLg": { status: "active", description: "Large callout style." },

  // ── Typography sizes ──────────────────────────────────────────────────
  "typography.sizes.xs": { status: "active", description: "Extra-small — captions, status pills." },
  "typography.sizes.sm": { status: "active", description: "Small — secondary UI text." },
  "typography.sizes.base": { status: "active", description: "Base UI size." },
  "typography.sizes.ui": { status: "active", description: "UI text — slightly larger than base." },
  "typography.sizes.md": { status: "active", description: "Medium — body copy." },
  "typography.sizes.lg": { status: "active", description: "Large — sub-headings." },
  "typography.sizes.h3": { status: "active", description: "Heading 3 size." },
  "typography.sizes.h2": { status: "active", description: "Heading 2 size." },
  "typography.sizes.h1": { status: "active", description: "Heading 1 size." },
  "typography.sizes.lessonTitle": { status: "active", description: "Lesson title size." },

  // ── Typography weights ────────────────────────────────────────────────
  "typography.weights.light": { status: "active", description: "Light — display/callout." },
  "typography.weights.regular": { status: "active", description: "Regular — body copy." },
  "typography.weights.medium": { status: "active", description: "Medium — emphasis without heading weight." },
  "typography.weights.semibold": { status: "active", description: "Semibold — UI emphasis, labels." },
  "typography.weights.bold": { status: "active", description: "Bold — headings." },

  // ── Typography line heights ───────────────────────────────────────────
  "typography.lineHeights.normal": { status: "active", description: "Browser default — used by most heading scales." },
  "typography.lineHeights.lesson": { status: "active", description: "Fixed line height for lesson titles." },
  "typography.lineHeights.lessonMobile": { status: "active", description: "Fixed line height for lesson titles on mobile." },
  "typography.lineHeights.scale": { status: "active", description: "Multiplier for body copy on lesson surfaces." },
  "typography.lineHeights.uiSoft": { status: "active", description: "Soft UI multiplier — labels, secondary text." },
  "typography.lineHeights.tight": { status: "active", description: "Tight multiplier — page titles." },

  // ── Typography mobile ─────────────────────────────────────────────────
  "typography.mobile.h1": { status: "active", description: "H1 on mobile breakpoint." },
  "typography.mobile.h2": { status: "active", description: "H2 on mobile breakpoint." },
  "typography.mobile.h3": { status: "active", description: "H3 on mobile breakpoint." },
  "typography.mobile.h4": { status: "active", description: "H4 on mobile breakpoint." },
  "typography.mobile.body": { status: "active", description: "Body on mobile breakpoint." },
  "typography.mobile.display": { status: "active", description: "Display on mobile breakpoint." },
  "typography.mobile.callout": { status: "active", description: "Callout on mobile breakpoint." },
  "typography.mobile.lessonTitle": { status: "active", description: "Lesson title on mobile breakpoint." },
  "typography.mobile.calloutSm": { status: "active", description: "Small callout on mobile breakpoint." },
  "typography.mobile.calloutMd": { status: "active", description: "Medium callout on mobile breakpoint." },
  "typography.mobile.calloutLg": { status: "active", description: "Large callout on mobile breakpoint." },

  // ── Spacing ───────────────────────────────────────────────────────────
  "spacing.none": { status: "active", description: "0px — explicit absence of space." },
  "spacing.xs": { status: "active", description: "Extra small — tight inline gaps." },
  "spacing.sm": { status: "active", description: "Small — control internal padding." },
  "spacing.md": { status: "active", description: "Medium — default control padding." },
  "spacing.lg": { status: "active", description: "Large — card padding, default block gap." },
  "spacing.lgPlus": {
    status: "active",
    description: "Mid-step between lg (16) and xl (24). Used to bridge the visual jump.",
  },
  "spacing.xl": { status: "active", description: "Extra large — section padding." },
  "spacing.2xl": { status: "active", description: "2xl — page-level gaps." },
  "spacing.3xl": { status: "active", description: "3xl — large layout gaps." },
  "spacing.4xl": { status: "active", description: "4xl — section separation." },
  "spacing.5xl": { status: "active", description: "5xl — hero spacing." },
  "spacing.6xl": { status: "active", description: "6xl — chapter-level separation." },
  "spacing.7xl": { status: "active", description: "7xl — full-bleed feature spacing." },

  // ── Layout ────────────────────────────────────────────────────────────
  "layout.mobileBreakpoint": { status: "active", description: "Pixel breakpoint switching to mobile typography/layout." },
  "layout.contentMaxWidth": {
    status: "active",
    description: "Outer max-width for course-section content. Hosts the 10-column grid.",
  },
  "layout.sectionPaddingY.desktop": { status: "active", description: "Vertical lesson section padding (desktop)." },
  "layout.sectionPaddingY.mobile": { status: "active", description: "Vertical lesson section padding (mobile)." },
  "layout.sectionPaddingX.desktop": { status: "active", description: "Horizontal lesson section padding (desktop)." },
  "layout.sectionPaddingX.mobile": { status: "active", description: "Horizontal lesson section padding (mobile)." },
  "layout.sidebar.width": { status: "active", description: "Editor sidebar width (percentage)." },
  "layout.sidebar.minWidth": { status: "active", description: "Editor sidebar min-width." },
  "layout.sidebar.maxWidth": { status: "active", description: "Editor sidebar max-width." },

  // ── Radius ────────────────────────────────────────────────────────────
  "radius.xs": { status: "active", description: "Extra small — pills, tight chips." },
  "radius.sm": { status: "active", description: "Small — controls, inputs." },
  "radius.md": { status: "active", description: "Medium — cards, panels." },
  "radius.lg": { status: "active", description: "Large — modal containers." },
  "radius.xl": { status: "active", description: "Extra large — feature surfaces." },
  "radius.2xl": { status: "active", description: "2xl — hero surfaces." },
  "radius.full": { status: "active", description: "Pill / circular." },

  // ── Shadow ────────────────────────────────────────────────────────────
  "shadow.sm": { status: "active", description: "Subtle separation — pressed states, tight cards." },
  "shadow.md": { status: "active", description: "Default raised surface." },
  "shadow.lg": { status: "active", description: "Floating elements — popovers." },
  "shadow.xl": { status: "active", description: "Modal / dialog elevation." },
  "shadow.itemPill": {
    status: "active",
    description: "Resting elevation for draggable interaction item-pills (Categorization items, Single Choice options) and popover containers.",
    figmaName: "AA-147 / Option button — Default",
  },
  "shadow.itemPillDrag": {
    status: "active",
    description: "Active-drag elevation for draggable interaction item-pills — slightly heavier than the resting shadow.",
    figmaName: "AA-147 / Option button — Drag",
  },

  // ── UI / chrome ────────────────────────────────────────────────────────
  "ui.fixedAccent": {
    status: "active",
    description:
      "Fixed interactive blue for UI chrome and selection states (Likert/Single Choice selection, learner toolbar, audio controls, focus borders, lesson-color fallbacks). Deliberately outside account theming — never tracks brand.accent (NA-242).",
  },
  "ui.fixedAccentHover": {
    status: "active",
    description: "Hover state for ui.fixedAccent surfaces (e.g. Save & Exit on Course Completion).",
  },
  "ui.selectionBorder": { status: "active", description: "Editor selection outline color." },
  "ui.focusRing": { status: "active", description: "Keyboard focus ring color." },
  "ui.focusRingOffset": { status: "active", description: "Focus ring offset color (typically the page background)." },
  "ui.workflowDraft": { status: "active", description: "Workflow badge — draft." },
  "ui.workflowReview": { status: "active", description: "Workflow badge — in review." },
  "ui.workflowApproved": { status: "active", description: "Workflow badge — approved." },
  "ui.workflowPublished": { status: "active", description: "Workflow badge — published." },
  "ui.workflowArchived": { status: "active", description: "Workflow badge — archived." },
  "ui.errorBorder": { status: "active", description: "Form-error border color." },
  "ui.errorBackground": { status: "active", description: "Form-error background fill." },
  "ui.errorText": { status: "active", description: "Form-error text color." },
  "ui.overlayMarker": { status: "active", description: "Overlay marker indicator on customized blocks." },
  "ui.overlayMarkerBackground": { status: "active", description: "Overlay marker background." },
  "ui.accountAssignmentAction": { status: "active", description: "Account assignment primary action and assigned check color." },
  "ui.accountAssignmentCancelText": { status: "active", description: "Account assignment cancel button text color." },
  "ui.accountAssignmentOverlay": { status: "active", description: "Account assignment modal overlay fill." },
  "ui.transitionDefault": { status: "active", description: "Default transition timing for hover/focus changes." },
  "ui.transitionSlow": { status: "active", description: "Slower transition timing for layout/dialog changes." },

  // ── Navigation (editor sidebar course nav) ────────────────────────────
  "navigation.borderColor": { status: "active", description: "Sidebar nav internal border." },
  "navigation.titleColor": { status: "active", description: "Sidebar nav section title color." },
  "navigation.emptyColor": { status: "active", description: "Sidebar nav placeholder text (empty list)." },
  "navigation.itemColor": { status: "active", description: "Sidebar nav item idle color." },
  "navigation.itemActiveColor": { status: "active", description: "Sidebar nav item active color." },
  "navigation.itemActiveBackground": { status: "active", description: "Sidebar nav item active background fill." },
  "navigation.itemLockedOpacity": { status: "active", description: "Opacity applied to locked sidebar nav items." },
  "navigation.titleFontSize": { status: "active", description: "Sidebar nav section title font size." },
  "navigation.itemFontSize": { status: "active", description: "Sidebar nav item font size." },
  "navigation.titlePadding": { status: "active", description: "Sidebar nav title padding." },
  "navigation.listPadding": { status: "active", description: "Sidebar nav list padding." },
  "navigation.itemPadding": { status: "active", description: "Sidebar nav item padding." },
  "navigation.itemGap": { status: "active", description: "Sidebar nav item gap." },
  "navigation.itemBorderRadius": { status: "active", description: "Sidebar nav item border radius." },
  "navigation.lockIconSize": { status: "active", description: "Sidebar nav lock icon size." },
};

export function getTokenMetadata(path: string): TokenMetadata {
  return TOKEN_METADATA[path] ?? DEFAULT_METADATA;
}

export function getAllTokenMetadata(): ReadonlyArray<{ path: string; metadata: TokenMetadata }> {
  return Object.entries(TOKEN_METADATA).map(([path, metadata]) => ({ path, metadata }));
}

export function countByStatus(): Record<TokenStatus, number> {
  const counts: Record<TokenStatus, number> = {
    active: 0,
    proposed: 0,
    deprecated: 0,
    "needs-review": 0,
  };
  for (const { metadata } of getAllTokenMetadata()) {
    counts[metadata.status] += 1;
  }
  return counts;
}
